/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Menu as MenuAnt, MenuProps as MenuAntProps } from 'antd';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MenuItems } from './menu-list';
import { getFirstPathnameSegment, getPathnameSegment } from '@/utils/common';

type LevelKeysProps = {
  key?: string;
  children?: LevelKeysProps[];
};

type MenuProps = {
  collapsed: boolean;
};

const getLevelKeys = (items1: LevelKeysProps[]) => {
  const key: Record<string, number> = {};
  const func = (items2: LevelKeysProps[], level = 1) => {
    items2.forEach((item) => {
      if (item.key) {
        key[item.key] = level;
      }
      if (item.children) {
        func(item.children, level + 1);
      }
    });
  };
  func(items1);
  return key;
};

export const Menu = ({ collapsed }: MenuProps) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [selectedKey, setSelectedKey] = useState<string>(pathname);
  const [stateOpenKeys, setStateOpenKeys] = useState([
    getFirstPathnameSegment(pathname),
    pathname
  ]);

  const items = MenuItems();
  const levelKeys = getLevelKeys(items as LevelKeysProps[]);

  const onOpenChange: MenuAntProps['onOpenChange'] = (openKeys) => {
    const currentOpenKey = openKeys.find(
      (key) => stateOpenKeys.indexOf(key) === -1
    );
    if (currentOpenKey !== undefined) {
      const repeatIndex = openKeys
        .filter((key) => key !== currentOpenKey)
        .findIndex((key) => levelKeys[key] === levelKeys[currentOpenKey]);

      setStateOpenKeys(
        openKeys
          .filter((_, index) => index !== repeatIndex)
          .filter((key) => levelKeys[key] <= levelKeys[currentOpenKey])
      );
    } else {
      setStateOpenKeys(openKeys);
    }
  };

  const handleMenuItemClick = (path: string) => {
    setSelectedKey(path);
    navigate(path);
  };

  useEffect(() => {
    setSelectedKey(pathname);

    const openKeys = [];
    for (let i = 0; i < 2; i++) {
      const segment = getPathnameSegment(pathname, i);
      if (segment) openKeys.push(segment);
    }

    if (collapsed) {
      setStateOpenKeys([]);
    } else {
      setStateOpenKeys(openKeys);
    }
  }, [pathname, collapsed]);


  return (
    <MenuAnt
      css={rootStyles(collapsed)}
      theme='light'
      mode='inline'
      openKeys={stateOpenKeys}
      selectedKeys={[selectedKey]}
      defaultSelectedKeys={[`${items[0]?.key}`]}
      defaultOpenKeys={[`${items[0]?.key}`]}
      items={items}
      onSelect={(k) => handleMenuItemClick(k.key)}
      onOpenChange={onOpenChange}
    />
  );
};

const rootStyles = (collapsed: boolean) => css`
  height: calc(100% - var(--header-height));

  ${!collapsed &&
  css`
    .ant-menu-item,
    .ant-menu-submenu-title {
      white-space: normal;
      word-wrap: break-word;
      overflow-wrap: break-word;
      line-height: 2rem !important;
    }
  `}
`;
